"use client";

import { useContext, useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { TAccordion } from "@/app/types/types";
import { screenSizeContext } from "@/app/store/context/screenSizesContext";
import { loadingContext } from "@/app/store/context/isLoadingContext";

gsap.registerPlugin(ScrollTrigger);

// ^ How To Use: you add the your components wrapped in the Accordion component
// ^ (collapsed height 6:88rem)

const Accordion = ({
	gapValue = 22,
	gapValueMobile = 32,
	children,
	activeAccordion,
	setActiveAccordion,
	index,
	action,
	className
}: TAccordion) => {
	const { isIpad, isReady } = useContext(screenSizeContext);
	const {isPageFetched} = useContext(loadingContext);

	const el = useRef<HTMLDivElement | null>(null);
	const body = useRef<HTMLDivElement | null>(null);

	const [inView, setInView] = useState(false);
	
	const gap = isIpad ? gapValueMobile : gapValue;

	const handleToggle = () => {
		setActiveAccordion(activeAccordion === index ? -1 : index);
		if (action) action();
	};

	useEffect(() => {
		if (!isReady || !inView) return;

			const inActive = activeAccordion === -1;

			if (inActive || index <= activeAccordion) {
				gsap.to(el.current, {
					ease: "power4.out",
					y: "0",
					duration: 0.75
				})
			} else if (index > activeAccordion) {


				gsap.to(el.current, {
					ease: "power4.out",
					y: `${gap}rem`,
					duration: 0.75
				})
			};
	}, [inView, activeAccordion, gap, index, isReady]);

	useEffect(() => {
		if (!isReady || isPageFetched !== "fetched") return;

			gsap.set(body.current, { y: `${gap}rem` });
			
			const trigger = ScrollTrigger.create({
				trigger: el.current,
				start: "top bottom",
				onEnter: () => {
					gsap.to(body.current, {
					ease: "power4.out",
					y: 0,
					duration: 0.85,
					onComplete: () => {
						setInView(true);
						trigger.kill();
					}
				})
			}
		});
		
		return () => {
			trigger.kill();
		}
	}, [isPageFetched, isReady, gap]);

	return (
		<div className={`accordion cursor-pointer relative ${className}`}
			style={{
				zIndex: index,
				marginTop: `-${index ? gap : 0}rem`,
			}}
			ref={el}
		>
			<div ref={body} className={`${className && "h-full"} accordion-body overflow-hidden`} onClick={handleToggle}>
				{children}
			</div>
		</div>
	);
};

export default Accordion;